import { defineStore } from 'pinia';
import { Cookie } from '@nuxt-common/util/app/utils/cookie';
import { getUnifiedSummaryErrorMessage, getAssociationErrorMessage } from '../utils/ErrorMessageMapper';
import { UNIFIED_SUMMARY_ERROR_MESSAGE, brand, ASSOCIATION_PAGE } from 'config';
import { formatTierName } from '../utils/formatTierName';
import sessionTimeoutRedirectHandler from '../utils/SessionTimeoutRedirectHandler';
import getHeader from '../utils/getHeader';
import emailVerifyModal from '../utils/emailVerifyModal';
import { fireGlobalErrorTag } from '../utils/eventsTagUtil';

import $http from '@nuxt-common/util/app/utils/http';

const { ERROR_MESSAGE, ENROLL_ERR_CODE, LOYALLIST_ERROR_MESSAGE } = ASSOCIATION_PAGE;
const UNIFIED_LOYALTY_ENDPOINT = '/xapi/loyalty/v1/unified/summary';
const DISCOUNT_ENDPOINT = '/account-xapi/api/creditaquisitionservice/discount?_deviceType=';
const REMOVE_LOYALTY_ENDPOINT = '/xapi/loyalty/v1/removeloyalty';
const DISASSOCIATE_ENDPOINT = '/xapi/loyalty/v1/disassociate';
const TRIPLE_POINT_ENDPOINT = '/xapi/loyalty/v1/pickyourdays';
const TRIPLE_POINT_DATE_ENDPOINT = '/xapi/loyalty/v1/pickyourdays';
const ACCOUNT_ASSOCIATION_ENDPOINT = '/xapi/loyalty/v1/accountassociation';
const STAR_REWARDS_ENROLLMENT_ENDPOINT = '/xapi/loyalty/v1/starrewardsenrollment';
const LOYALLIST_ENROLLMENT_ENDPOINT = '/xapi/loyalty/v1/loyallistenrollment';
const PUBLISH_EVENT_ENDPOINT = '/xapi/loyalty/v1/publishevent';
const EMAIL_VERIFICATION_ENDPOINT = '/xapi/loyalty/v1/accountassociation';
const OTP_VERIFICATION_ENDPOINT = '/xapi/loyalty/v1/accountassociation';
const GENERIC_ERROR_MESSAGE = "We're sorry, but we cannot display your information at this time.";

const runIfCreateAccount = (state, callback) => {
  if (state.fromLoyaltyCreateAccount) {
    callback();
  }
};

const handleErrorTag = (state, errorCode, errorMessage) => {
  runIfCreateAccount(state, () => fireGlobalErrorTag(errorCode, errorMessage));
};

const handleAssociationError = (state, { errorDetails, associationFlowBy, errorField, skipSessionHandler }) => {
  const errorObject = errorDetails?.error?.[0];
  if (!skipSessionHandler) {
    sessionTimeoutRedirectHandler(errorObject);
  }
  const { errorCode } = errorObject || {};
  const errorMessage = getAssociationErrorMessage(associationFlowBy, errorObject, LOYALLIST_ERROR_MESSAGE);
  handleErrorTag(state, errorCode, errorMessage);
  state[errorField] = errorMessage;
};

const setPageActionHeader = (state, headers) => {
  runIfCreateAccount(state, () => {
    headers.page_origin = 'create_account';
  });
};

export const useLoyaltyHubStore = defineStore('loyaltyHub', {
  state: () => ({
    unifiedLoyalty: null,
    tierName: '',
    estimatedTierCode: '',
    unifiedLoyaltyError: null,
    isLtyLinked: false,
    unifiedLoyaltyGlobalError: null,
    discountPercentage: null,
    loyaltyRemoved: null,
    loyaltyRemovedError: null,
    triplePointData: {},
    offers: [],
    requestedDates: {},
    pageError: {
      visible: false,
      message: null,
    },
    dateToDelete: null,
    loyallistWithCcOrLidOrPhone: null,
    loyallistWithCcOrLidOrPhoneError: '',
    accountAssociationResponse: null,
    accountAssociationError: null,
    loyallistFlow: '',
    showSpinnerforCreateAcctFlow: false,
    starRewardsWithoutCC: null,
    starRewardsWithoutCCError: null,
    loyallistEnrollmentError: null,
    loyallistEnrolled: null,
    isLoyallistEnrollSubmitted: false,
    emailVerification: null,
    emailVerificationError: null,
    otpVerification: null,
    otpVerificationError: null,
    associationFields: {},
    fromLoyaltyCreateAccount: false,
  }),
  getters: {
    userAssociation: (state) => {
      if (state.unifiedLoyalty !== null) {
        return state.unifiedLoyalty.points.loyaltyPointsResponse.loyaltyInfo.associated;
      }
      return undefined;
    },
  },
  actions: {
    async fetchUnifiedLoyaltySummary({ headers }) {
      try {
        const response = await $http.get(UNIFIED_LOYALTY_ENDPOINT, { headers });
        const loyaltyErrors = response.data?.loyaltyHubUnifiedResponse?.errors;
        const bindingErrors = response.data?.ErrorsBinding;
        const genericErrors = response.data?.errors;
        const errorDetails = loyaltyErrors || bindingErrors || genericErrors;

        if (errorDetails) {
          const errorObject = errorDetails.error?.[0];
          this.unifiedLoyaltyGlobalError = getUnifiedSummaryErrorMessage(errorObject, UNIFIED_SUMMARY_ERROR_MESSAGE);
        } else {
          this.unifiedLoyalty = response.data.loyaltyHubUnifiedResponse;
          this.tierName = formatTierName(response.data?.loyaltyHubUnifiedResponse?.points?.loyaltyPointsResponse?.loyaltyInfo?.tier?.tierName);
          this.estimatedTierCode = formatTierName(
            response.data?.loyaltyHubUnifiedResponse?.points?.loyaltyPointsResponse?.loyaltyInfo?.tier?.estimatedTierCode,
          );
          this.unifiedLoyaltyError = response.data?.errors;
          this.isLtyLinked = response.data?.loyaltyHubUnifiedResponse?.points?.loyaltyPointsResponse?.loyaltyInfo?.profile?.isLtyLinked;
          this.unifiedLoyaltyGlobalError = null;
        }
      } catch (err) {
        const loyaltyErrors = err?.response?.data?.loyaltyHubUnifiedResponse?.errors;
        const bindingErrors = err?.response?.data?.ErrorsBinding;
        const genericErrors = err?.response?.data?.errors;
        const errorDetails = loyaltyErrors || bindingErrors || genericErrors;
        const errorObject = errorDetails?.error?.[0];
        this.unifiedLoyaltyGlobalError = getUnifiedSummaryErrorMessage(errorObject, UNIFIED_SUMMARY_ERROR_MESSAGE);
      }
    },
    async fetchDiscountDetails(data) {
      try {
        const { deviceType } = data;
        const response = await $http.get(DISCOUNT_ENDPOINT + deviceType);
        this.discountPercentage = response.data?.DiscountResponse?.discount?.discountPercentage || 20;
      } catch {
        this.discountPercentage = '';
      }
    },
    async fetchRemoveLoyalty({ headers, params }) {
      try {
        const config = { headers, params };
        const response = await $http.delete(REMOVE_LOYALTY_ENDPOINT, config);
        this.loyaltyRemoved = response.data;
        this.loyaltyRemovedError = response.data?.errors || null;
      } catch (err) {
        this.loyaltyRemoved = { errors: err };
        this.loyaltyRemovedError = err;
      }
    },
    async disassociateLoyalty({ headers, params }) {
      try {
        const config = { headers, params };
        const response = await $http.get(DISASSOCIATE_ENDPOINT, config);
        this.loyaltyRemoved = response.data;
        this.loyaltyRemovedError = response.data?.errors || null;
      } catch (err) {
        this.loyaltyRemoved = { errors: err };
        this.loyaltyRemovedError = err;
      }
    },
    async fetchTriplePoint({ headers }) {
      const config = {
        headers,
      };
      await $http
        .get(TRIPLE_POINT_ENDPOINT, config)
        .then((response) => {
          this.triplePointData = { ...this.triplePointData, ...response.data.PickYourDaySummary };
          if (response.data.PickYourDaySummary.programOffers) {
            this.offers = response.data.PickYourDaySummary.programOffers.offer;
          }
          response?.data?.PickYourDaySummary?.accountOffers?.PADAvailability[0]?.PADScheduledDays?.map((element) => {
            const params = {
              PickYourDaySummary: {
                offerCode: element.offerCode,
                requestedDate: element.date,
              },
            };
            const { requestedDate, offerCode } = params.PickYourDaySummary;
            this.requestedDates[requestedDate] = offerCode;
            return true;
          });
        })
        .catch((err) => {
          const errorCode = err?.response?.data?.errors?.error[0]?.errorCode;
          if (
            (errorCode && errorCode.toString() === '45001') ||
            (errorCode && errorCode.toString() === '45006') ||
            (errorCode && errorCode.toString() === '45201')
          ) {
            window.location.href = '/account/signin-loyalty';
          }
        });
    },
    async postTriplePointDate({ headers, params }) {
      await $http
        .post(TRIPLE_POINT_DATE_ENDPOINT, { ...params }, { headers })
        .then((response) => {
          const error = {
            visible: false,
            message: null,
          };
          this.pageError = { ...this.pageError, ...error };
          this.triplePointData = { ...this.triplePointData, ...response.data.PickYourDaySummary };
          const { requestedDate, offerCode } = params.PickYourDaySummary;
          this.requestedDates[requestedDate] = offerCode;
        })
        .catch((err) => {
          // prepare page specific inline error
          const errorCode = err?.response?.data?.errors?.error[0]?.errorCode;
          let customMessage = '';
          if (errorCode && errorCode.toString() === '45202') {
            customMessage = "We're sorry! Looks like we're experiencing some technical difficulties. Please try again later.";
          } else if (
            (errorCode && errorCode.toString() === '45001') ||
            (errorCode && errorCode.toString() === '45006') ||
            (errorCode && errorCode.toString() === '45201')
          ) {
            window.location.href = '/account/signin-loyalty';
          }
          const error = {
            visible: true,
            message: customMessage || GENERIC_ERROR_MESSAGE,
          };
          this.pageError = { ...this.pageError, ...error };
        });
    },
    async deleteTriplePointDate({ headers, deleteScheduleParams }) {
      const config = {
        headers,
        data: deleteScheduleParams,
      };
      $http
        .delete(TRIPLE_POINT_DATE_ENDPOINT, config)
        .then((response) => {
          const error = {
            visible: false,
            message: null,
          };
          this.pageError = { ...this.pageError, ...error };
          this.triplePointData = { ...this.triplePointData, ...response.data.PickYourDaySummary };
          delete this.requestedDates[deleteScheduleParams.PickYourDaySummary.requestedDate];
          this.dateToDelete = null;
        })
        .catch((err) => {
          // prepare page specific inline error
          const errorCode = err?.response?.data?.errors?.error[0]?.errorCode;
          let customMessage = '';
          if (errorCode && errorCode.toString() === '45202') {
            customMessage = "We're sorry! Looks like we're experiencing some technical difficulties. Please try again later.";
          } else if (
            (errorCode && errorCode.toString() === '45001') ||
            (errorCode && errorCode.toString() === '45006') ||
            (errorCode && errorCode.toString() === '45201')
          ) {
            window.location.href = '/account/signin-loyalty';
          }
          const error = {
            visible: true,
            message: customMessage || GENERIC_ERROR_MESSAGE,
          };
          this.pageError = { ...this.pageError, ...error };
        });
    },
    postLoyallistWithCcOrLidOrPhone({ headers, LoyaltyAccountInfo, associationFlowBy }) {
      this.loyallistFlow = associationFlowBy;
      const data = {
        LoyaltyAccountInfo,
      };
      return $http
        .post(ACCOUNT_ASSOCIATION_ENDPOINT, data, { headers })
        .then((response) => {
          const loyaltyErrors = response.data?.loyalty?.errors;
          const bindingErrors = response.data?.ErrorsBinding;
          const genericErrors = response.data?.errors;
          const errorDetails = loyaltyErrors || bindingErrors || genericErrors;

          if (errorDetails) {
            handleAssociationError(this, {
              errorDetails,
              associationFlowBy,
              errorField: 'loyallistWithCcOrLidOrPhoneError',
            });
          } else {
            this.loyallistWithCcOrLidOrPhone = { payload: response.data, LoyaltyAccountInfo };
            this.loyallistWithCcOrLidOrPhoneError = null;
          }
        })
        .catch((err) => {
          const loyaltyErrors = err?.response?.data?.loyalty?.errors;
          const bindingErrors = err?.response?.data?.ErrorsBinding;
          const genericErrors = err?.response?.data?.errors;
          const errorDetails = loyaltyErrors || bindingErrors || genericErrors;
          handleAssociationError(this, {
            errorDetails,
            associationFlowBy,
            errorField: 'loyallistWithCcOrLidOrPhoneError',
            skipSessionHandler: true,
          });
        });
    },
    async autoManageLoyaltyMembership() {
      this.fromLoyaltyCreateAccount = true;
      const sessionStorage = JSON.parse(window.sessionStorage.getItem('loyaltyUserData'));

      const publishEventPayload = {
        eventDetails: {
          eventType: 'CREATE_PROFILE',
          deviceTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };
      const LoyaltyAccountInfo = sessionStorage;
      const headers = await getHeader(Cookie, brand);
      Object.assign(headers, { page_origin: 'create_account' });

      this.loyallistWithCcOrLidOrPhoneError = '';
      const actionMap = {
        associateByPhoneNumber: 'PHONE',
        lookUpByPropOrCoBrandCard: 'CC',
        associateByLoyaltyNumber: 'LID',
      };
      const associationFlowBy = actionMap[sessionStorage.action];
      if (!associationFlowBy) return;

      await this.postLoyallistWithCcOrLidOrPhone({
        headers,
        LoyaltyAccountInfo,
        associationFlowBy,
      });

      if (!this.loyallistWithCcOrLidOrPhoneError) {
        await this.publishEvent({ headers, publishEventPayload });
        this.associationFields = LoyaltyAccountInfo;
        await emailVerifyModal(this);
        this.showSpinnerforCreateAcctFlow = false;
        handleErrorTag(this, {});
        this.loyallistWithCcOrLidOrPhoneError = null;
        window.sessionStorage.removeItem('loyaltyUserData');
      }
    },
    enrollStarRewardsWithoutCC({ headers, loyalty }) {
      const data = {
        loyalty,
      };
      return $http
        .post(STAR_REWARDS_ENROLLMENT_ENDPOINT, data, { headers })
        .then((response) => {
          if (response.data?.errors) {
            const message = response?.data?.errors?.error?.[0]?.message || ERROR_MESSAGE;
            this.starRewardsWithoutCCError = { message };
            const errorCode = response?.data?.errors?.error?.[0]?.errorCode || ENROLL_ERR_CODE;
            this.loyallistEnrollmentError = { errorCode };
          } else {
            this.starRewardsWithoutCC = { payload: response.data, loyalty };
            this.starRewardsWithoutCCError = null;
            this.loyallistEnrollmentError = null;
          }
        })
        .catch((err) => {
          const message = err?.response?.data?.errors?.error?.[0]?.message || ERROR_MESSAGE;
          this.starRewardsWithoutCCError = { message };
          const errorCode = err?.response?.data?.errors?.error?.[0]?.errorCode || ENROLL_ERR_CODE;
          this.loyallistEnrollmentError = { errorCode };
        });
    },
    postLoyallistEnrollment({ headers, loyalty }) {
      const data = {
        loyalty,
      };
      return $http
        .post(LOYALLIST_ENROLLMENT_ENDPOINT, data, { headers })
        .then((response) => {
          if (response.data?.errors) {
            const errorCode = response.data?.errors?.error?.[0]?.errorCode || 'Default';
            this.loyallistEnrollmentError = errorCode;
          } else {
            this.loyallistEnrolled = response.data;
            this.loyallistEnrollmentError = null;
          }
        })
        .catch((err) => {
          this.loyallistEnrollmentError = err?.response?.data?.errors?.error[0]?.errorCode || 'Default';
        });
    },
    publishEvent({ headers, publishEventPayload }) {
      return $http.post(PUBLISH_EVENT_ENDPOINT, publishEventPayload, { headers }).catch((err) => {
        console.log(err);
      });
    },
    postEmailVerification({ headers, LoyaltyAccountInfo, associationFlowBy }) {
      headers.Action = 'verifyEmail';
      const data = {
        LoyaltyAccountInfo,
      };
      setPageActionHeader(this, headers);
      return $http
        .post(EMAIL_VERIFICATION_ENDPOINT, data, { headers })
        .then((response) => {
          const loyaltyErrors = response.data?.loyalty?.errors;
          const bindingErrors = response.data?.ErrorsBinding;
          const genericErrors = response.data?.errors;
          const errorDetails = loyaltyErrors || bindingErrors || genericErrors;

          if (errorDetails) {
            handleAssociationError(this, {
              errorDetails,
              associationFlowBy,
              errorField: 'emailVerificationError',
            });
          } else {
            this.emailVerification = response.data;
            this.emailVerificationError = null;
          }
        })
        .catch((err) => {
          const loyaltyErrors = err?.response?.data?.loyalty?.errors;
          const bindingErrors = err?.response?.data?.ErrorsBinding;
          const genericErrors = err?.response?.data?.errors;
          const errorDetails = loyaltyErrors || bindingErrors || genericErrors;
          handleAssociationError(this, {
            errorDetails,
            associationFlowBy,
            errorField: 'emailVerificationError',
            skipSessionHandler: true,
          });
        });
    },
    postOtpVerification({ headers, LoyaltyAccountInfo, associationFlowBy }) {
      const data = {
        LoyaltyAccountInfo,
      };
      setPageActionHeader(this, headers);
      return $http
        .post(OTP_VERIFICATION_ENDPOINT, data, { headers })
        .then((response) => {
          const loyaltyErrors = response.data?.loyalty?.errors;
          const bindingErrors = response.data?.ErrorsBinding;
          const genericErrors = response.data?.errors;
          const errorDetails = loyaltyErrors || bindingErrors || genericErrors;

          if (errorDetails) {
            handleAssociationError(this, {
              errorDetails,
              associationFlowBy,
              errorField: 'otpVerificationError',
            });
          } else {
            this.otpVerification = response.data;
            this.otpVerificationError = null;
          }
        })
        .catch((err) => {
          const loyaltyErrors = err?.response?.data?.loyalty?.errors;
          const bindingErrors = err?.response?.data?.ErrorsBinding;
          const genericErrors = err?.response?.data?.errors;
          const errorDetails = loyaltyErrors || bindingErrors || genericErrors;
          handleAssociationError(this, {
            errorDetails,
            associationFlowBy,
            errorField: 'otpVerificationError',
            skipSessionHandler: true,
          });
        });
    },
  },
});
